import { ReactNode, useMemo, useState } from "react";

import {
    UserVaultPositionExpanded,
    LendingVaultExpanded,
    UserVaultPositionStatus,
    usePortfolioRewardsUtils,
    useVaultApyBreakdown,
    getVaultLoopscalePointsFromStakeRewards,
    AggregatedUserExternalRewardsPnlResponse,
    convertExternalRewardsToAggregatedRewards,
    UserVaultExternalRewardsPnlResponse,
    useGetUserLpPositionAmounts
} from "@bridgesplit/abf-react";
import {
    MediaQuery,
    Row,
    ShadowCard,
    Text,
    ToggleChevron,
    Column,
    SPACING,
    UsdPriceTextWithTooltip
} from "@bridgesplit/ui";
import { Box, Collapse, useMediaQuery } from "@mui/material";
import { TokenSize, getTokenImageSize, TokenImage } from "app/components/common";
import {
    PortfolioLendVaultApy,
    RewardsPortfolioBorrowApyProps as RewardsPortfolioApyProps
} from "app/components/points";
import { calculateLendVaultDailyPoints } from "app/components/portfolio/stats";
import { VaultTagIcon } from "app/components/explore/vaults/VaultTitle";

import { VaultPositionStats } from "./VaultPositionStats";
import { VaultActions } from "./VaultActionButtons";

export function VaultPositionCard({
    vaultPosition,
    query,
    status,
    unclaimedVaultRewards,
    userExternalRewardsPosition
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
    unclaimedVaultRewards: boolean;
    userExternalRewardsPosition: UserVaultExternalRewardsPnlResponse | null | undefined;
    query: MediaQuery;
    status?: UserVaultPositionStatus;
}) {
    const [expandedState, setExpanded] = useState(false);

    const vaultExpanded = vaultPosition?.vaultExpanded;

    const isMobile = useMediaQuery(query.below);

    const expanded = expandedState || isMobile;

    const vaultExternalRewardsAggregated: AggregatedUserExternalRewardsPnlResponse | null | undefined = useMemo(() => {
        return convertExternalRewardsToAggregatedRewards(vaultExpanded, userExternalRewardsPosition);
    }, [vaultExpanded, userExternalRewardsPosition]);

    const { totalAmount, lpCollateralDeposits } = useGetUserLpPositionAmounts({
        userPosition: vaultPosition,
        vaultExpanded: vaultExpanded
    });

    const vaultAddress = vaultExpanded?.vault.address;

    return (
        <PortfolioPositionCardShell
            headerLeft={
                <>
                    <LendVaultImage lendVaultExpanded={vaultExpanded} size="md" />
                    <VaultTagIcon tags={vaultExpanded?.vaultMetadata?.tags} />
                    <Text loadingWidth="100px" loading={!vaultPosition}>
                        {vaultExpanded?.vaultMetadata?.name}
                    </Text>
                    <UsdPriceTextWithTooltip
                        loadingWidth="55px"
                        textColor="caption"
                        rawUsdValue={(totalAmount.amountUsd ?? 0) + (lpCollateralDeposits.amountUsd ?? 0)}
                        loading={!vaultPosition}
                    />
                </>
            }
            headerRight={
                <Row sx={{ [query.below]: { display: "none" } }} spacing={1}>
                    {vaultPosition ? (
                        totalAmount.amountPrincipal !== null ? (
                            <VaultPositionApy vaultPosition={vaultPosition} />
                        ) : (
                            <Text color="disabled"> Closed </Text>
                        )
                    ) : null}
                    <ToggleChevron disabled={!vaultPosition} textVariant="h2" expanded={expanded} />
                </Row>
            }
            expanded={expanded}
            onToggle={vaultPosition ? () => setExpanded((prev) => !prev) : undefined}
        >
            <VaultPositionStats
                vaultPosition={vaultPosition}
                query={query}
                status={status}
                externalRewards={vaultAddress ? vaultExternalRewardsAggregated?.[vaultAddress] : undefined}
            />
            <VaultActions
                vaultPosition={vaultPosition}
                vaultExpanded={vaultExpanded}
                hasUnclaimedVaultRewards={!!unclaimedVaultRewards}
                isCondensed={isMobile}
            />
        </PortfolioPositionCardShell>
    );
}

export function PortfolioPositionCardShell({
    headerLeft,
    headerRight,
    expanded,
    onToggle,
    children
}: {
    headerLeft: ReactNode;
    headerRight: ReactNode;
    expanded: boolean;
    onToggle?: () => void;
    children: ReactNode;
}) {
    return (
        <ShadowCard sx={{ flexGrow: 1, overflow: "visible" }} padding={false}>
            <Row sx={{ p: 2, cursor: onToggle ? "pointer" : undefined }} onClick={onToggle} spaceBetween>
                <Row spacing={1}>{headerLeft}</Row>
                {headerRight}
            </Row>

            <Collapse in={expanded}>
                <Column sx={{ p: 2, pt: 0 }} spacing={2}>
                    {children}
                </Column>
            </Collapse>
        </ShadowCard>
    );
}

export function VaultPositionApy({
    vaultPosition,
    ...props
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
} & Partial<RewardsPortfolioApyProps>) {
    const identifier = vaultPosition?.vaultExpanded.vault.address;
    const { getAggregatedStakeRewards } = usePortfolioRewardsUtils();

    const apy = useVaultApyBreakdown({ lendingVaultExpanded: vaultPosition?.vaultExpanded });

    const stakeRewards = getAggregatedStakeRewards(vaultPosition?.vaultExpanded?.vault.address);
    const customDailyPoints = calculateLendVaultDailyPoints(stakeRewards);

    return (
        <PortfolioLendVaultApy
            isBorrower={false}
            loopscalePointsAction={getVaultLoopscalePointsFromStakeRewards(stakeRewards)}
            identifier={identifier}
            apy={apy?.baseApy}
            totalApy={vaultPosition?.totalApy}
            customDailyPoints={customDailyPoints}
            lendingVaultExpanded={vaultPosition?.vaultExpanded}
            {...props}
        />
    );
}

function LendVaultImage({
    lendVaultExpanded,
    size: propsSize,
    offset = -0.25
}: {
    lendVaultExpanded: LendingVaultExpanded | undefined;
    size: TokenSize;
    offset?: number;
}) {
    const size = getTokenImageSize(propsSize);

    const depositToken = lendVaultExpanded?.principalMetadata;

    return (
        <Box sx={{ position: "relative", width: size, height: size }}>
            <TokenImage
                size={propsSize}
                sx={{ position: "absolute", bottom: SPACING * offset, right: SPACING * offset, zIndex: 1 }}
                metadata={depositToken}
            />
        </Box>
    );
}
